import React from 'react';
import { SearchDropdown, SearchItem } from '@/components/ui/search-dropdown';
import type { Customer } from '@/types/firebase';

interface CustomerSearchProps {
  customers: Customer[];
  value: string;
  onChange: (value: string) => void;
  onSelect: (customer: Customer) => void;
  onAddNew?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CustomerSearch({
  customers,
  value,
  onChange,
  onSelect,
  onAddNew,
  placeholder = 'Search customers by name, email, or phone...',
  className = '',
  disabled = false
}: CustomerSearchProps) {
  // Convert customers to SearchItem format
  const customerItems: SearchItem[] = customers.map(customer => ({
    id: customer.id,
    name: customer.companyName || `${customer.firstName} ${customer.lastName}`,
    secondaryText: customer.companyName ? `${customer.firstName} ${customer.lastName}` : customer.email,
    originalData: customer
  }));

  // Custom filter function for customers
  const filterCustomers = (item: SearchItem, searchTerm: string) => {
    const term = searchTerm.toLowerCase();
    const customer = item.originalData as Customer;
    
    return (
      item.name.toLowerCase().includes(term) ||
      (customer.email && customer.email.toLowerCase().includes(term)) ||
      (customer.phone && customer.phone.toLowerCase().includes(term)) ||
      (customer.firstName && customer.firstName.toLowerCase().includes(term)) ||
      (customer.lastName && customer.lastName.toLowerCase().includes(term)) ||
      (customer.companyName && customer.companyName.toLowerCase().includes(term))
    );
  };

  // Custom renderer for customer items
  const renderCustomerItem = (item: SearchItem) => {
    const customer = item.originalData as Customer;
    
    return (
      <div
        key={item.id}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
        onClick={() => onSelect(customer)}
      >
        <div className="flex items-center">
          {customer.companyName ? (
            <div className="font-medium">
              {customer.companyName} - <span className="italic font-normal">{customer.firstName} {customer.lastName}</span>
            </div>
          ) : (
            <div className="font-medium">
              {customer.firstName} {customer.lastName}
            </div>
          )}
        </div>
        {customer.email && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {customer.email}
          </div>
        )}
      </div>
    );
  };

  return (
    <SearchDropdown
      items={customerItems}
      value={value}
      onChange={onChange}
      onSelect={(item) => onSelect(item.originalData as Customer)}
      onAddNew={onAddNew}
      addNewLabel="Add New Customer"
      placeholder={placeholder}
      emptyMessage="No customers found"
      className={className}
      disabled={disabled}
      renderItem={renderCustomerItem}
      filterFunction={filterCustomers}
    />
  );
}
